import { useState } from 'react';
import {
  Menu,
  X,
  Phone,
  Mail,
  Scissors,
  TreePine,
  Flower2,
  ShieldCheck,
  Clock3,
  MapPin,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';
import { Helmet } from 'react-helmet';
import './App.css';

const services = [
  {
    icon: <TreePine size={28} />,
    title: 'Élagage professionnel',
    description:
      'Taille, mise en sécurité et entretien des arbres avec une exécution précise et soignée.',
  },
  {
    icon: <Scissors size={28} />,
    title: 'Taille de haies',
    description:
      'Mise en forme élégante de vos haies et arbustes pour un jardin net toute l année.',
  },
  {
    icon: <Flower2 size={28} />,
    title: 'Entretien de jardin',
    description:
      'Tonte, nettoyage, désherbage et entretien régulier pour garder un espace extérieur impeccable.',
  },
];

const highlights = [
  {
    icon: <ShieldCheck size={22} />,
    title: 'Travail soigné',
    text: 'Finitions propres, gestes précis, respect total des lieux.',
  },
  {
    icon: <Clock3 size={22} />,
    title: 'Rapide et fiable',
    text: 'Délais clairs et interventions organisées, sans mauvaise surprise.',
  },
  {
    icon: <MapPin size={22} />,
    title: 'Bruxelles et alentours',
    text: 'Intervention locale avec une vraie disponibilité sur le terrain.',
  },
];

const processSteps = [
  'Prise de contact rapide par téléphone ou e-mail',
  'Évaluation claire de votre besoin',
  'Intervention soignée et propre',
  'Suivi possible pour un entretien régulier',
];

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <div className="site">
      <Helmet>
        <html lang="fr" />
        <title>Aklimbo | Jardinage et élagage</title>
        <meta
          name="description"
          content="Aklimbo, jardinier indépendant : élagage, taille de haies, entretien de jardin. Travail soigné, devis rapide."
        />
        <meta
          name="keywords"
          content="jardinier Bruxelles, élagage, taille de haies, entretien jardin, tonte pelouse, aklimbo, jardinage professionnel, service de jardinage, entretien espaces verts"
        />
        <meta name="theme-color" content="#243a2f" />
        <meta property="og:title" content="Aklimbo | Jardinage et élagage" />
        <meta
          property="og:description"
          content="Services de jardinage et d'élagage à Bruxelles. Intervention rapide et résultat soigné."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://aklimbo.be/" />
        <meta property="og:image" content="https://aklimbo.be/logo512.png" />
      </Helmet>

      <header className="topbar">
        <div className="container topbar-inner">
          <a href="#accueil" className="brand" onClick={closeMenu}>
            <img src="/logo.png" alt="Logo Aklimbo" className="brand-logo" />
            <div>
              <strong>Aklimbo</strong>
              <span>Jardinage et élagage</span>
            </div>
          </a>

          <nav className="desktop-nav" aria-label="Navigation principale">
            <a href="#services">Services</a>
            <a href="#methode">Méthode</a>
            <a href="#apropos">À propos</a>
            <a href="#contact">Contact</a>
          </nav>

          <button
            className="menu-btn"
            onClick={() => setIsMenuOpen((prev) => !prev)}
            aria-label="Ouvrir le menu"
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {isMenuOpen && (
          <nav className="mobile-nav" aria-label="Navigation mobile">
            <a href="#services" onClick={closeMenu}>
              Services
            </a>
            <a href="#methode" onClick={closeMenu}>
              Méthode
            </a>
            <a href="#apropos" onClick={closeMenu}>
              À propos
            </a>
            <a href="#contact" onClick={closeMenu}>
              Contact
            </a>
          </nav>
        )}
      </header>

      <main>
        <section id="accueil" className="hero">
          <div className="hero-bg-shape hero-bg-shape-1" />
          <div className="hero-bg-shape hero-bg-shape-2" />
          <div className="container hero-content">
            <h1>
              Un jardin propre, net et vivant,
              <br />
              sans stress.
            </h1>
            <p className="hero-text">
              Aklimbo prend soin de vos espaces verts avec un service humain, ponctuel et
              très soigné. Pour particuliers et professionnels.
            </p>

            <div className="hero-cta-row">
              <a className="btn btn-primary" href="tel:+32492774686">
                <Phone size={18} /> Appeler maintenant
              </a>
              <a className="btn btn-ghost" href="#services">
                <Sparkles size={18} /> Voir les services
              </a>
            </div>

            <div className="hero-highlights">
              {highlights.map((item) => (
                <article key={item.title}>
                  <div className="icon-wrap">{item.icon}</div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="services" className="section section-soft">
          <div className="container">
            <div className="section-heading">
              <p className="eyebrow">Services</p>
              <h2>Ce que je fais pour votre jardin</h2>
            </div>

            <div className="service-grid">
              {services.map((service) => (
                <article key={service.title} className="service-card">
                  <div className="service-icon">{service.icon}</div>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="methode" className="section">
          <div className="container split">
            <div>
              <p className="eyebrow">Méthode</p>
              <h2>Un process simple et efficace</h2>
              <p className="lead">
                L'objectif est d'être clair, rapide et efficace, du premier message à la
                fin de l'intervention.
              </p>
            </div>

            <div className="steps">
              {processSteps.map((step) => (
                <div key={step} className="step-item">
                  <CheckCircle2 size={18} />
                  <span>{step}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="apropos" className="section section-grid-bg">
          <div className="container about">
            <div>
              <p className="eyebrow">À propos</p>
              <h2>Un jardinier passionné et de confiance</h2>
              <p>
                Je traite chaque chantier avec la même exigence : respect, ponctualité
                et qualité visible. Mon objectif est que vous soyez fier de votre extérieur
                après chaque intervention.
              </p>
              <p>
                Que ce soit pour une intervention ponctuelle ou un entretien régulier,
                vous avez un interlocuteur unique, disponible et impliqué.
              </p>
            </div>

            <aside className="contact-card" id="contact">
              <img src="/logo.png" alt="Logo Aklimbo" className="about-logo" />
              <h3>Demande rapide</h3>
              <p>Une question, un besoin ou un devis ? Je réponds rapidement.</p>

              <a href="tel:+32492774686" className="contact-link">
                <Phone size={18} /> 0492 77 46 86
              </a>
              <a href="mailto:Aklimbo.mv@gmail.com" className="contact-link">
                <Mail size={18} /> Aklimbo.mv@gmail.com
              </a>
              <p className="muted">
                Zone d'intervention : Bruxelles et communes voisines.
              </p>
            </aside>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container footer-inner">
          <div>
            <strong>Aklimbo</strong>
            <p>Jardinage et élagage professionnels à Bruxelles.</p>
          </div>
          <div>
            <p>Téléphone : 0492 77 46 86</p>
            <p>Email: Aklimbo.mv@gmail.com</p>
          </div>
          <div>
            <p>© 2026 Aklimbo. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;